/**
 * Interface text for the Analytics 2.0 pages (executive, users, cohorts, features, receipts, price search, costs, retail).
 * Keys are prefixed "v2."; every English key needs its Serbian counterpart. Plural messages have _one, _few and _other.
 */
export const en = {
  // Shared
  'v2.trend.gaps': 'Days the source does not cover are gaps, not zeros.',
  'v2.col.feature': 'Feature',
  'v2.col.actions': 'Actions',
  'v2.col.accounts': 'Accounts',
  'v2.col.lastAction': 'Last action',
  'v2.col.week': 'Week',
  'v2.col.category': 'Category',
  'v2.col.chains': 'Chains',
  'v2.col.offers': 'Offers',
  'v2.col.products': 'Products',
  'v2.col.promoShare': 'Promo share',
  'v2.col.cost': 'Cost',
  'v2.col.source': 'Source',

  // Executive
  'v2.exec.title': 'Executive overview',
  'v2.exec.description': 'The headline numbers for the selected range, each against the period of the same length before it.',
  'v2.exec.devices': 'Devices',
  'v2.exec.devicesText': 'GA4 app installs. A guest is a device without an account.',
  'v2.exec.accounts': 'Accounts',
  'v2.exec.accountsText': 'Production Firebase accounts. Activity means a persisted product action, never a sign-in.',
  'v2.exec.product': 'Product',
  'v2.exec.productText': 'What people did with the app.',
  'v2.exec.health': 'Health and cost',
  'v2.exec.healthText': 'Cloud Billing is the authority for cost.',

  // Users
  'v2.users.title': 'Users',
  'v2.users.description': 'Accounts ranked by what they did in the app. Signing in is never counted as activity.',
  'v2.users.filters': 'Account filters',
  'v2.users.searchLabel': 'Search accounts',
  'v2.users.placeholderAdmin': 'E-mail or account id',
  'v2.users.placeholderAnalyst': 'Account key (first 4+ characters)',
  'v2.users.sort': 'Sort',
  'v2.users.sort.score': 'Activity score',
  'v2.users.sort.last_action': 'Last action',
  'v2.users.sort.created': 'Newest',
  'v2.users.sort.receipts': 'Receipts',
  'v2.users.population': 'Population',
  'v2.population.production': 'production',
  'v2.population.internal': 'internal',
  'v2.population.test': 'test',
  'v2.population.all': 'all',
  'v2.users.savedFilters': 'Saved filters',
  'v2.users.savedFiltersCount': 'Saved filters ({count})',
  'v2.users.presetName': 'Preset name',
  'v2.users.presetPlaceholder': 'Name',
  'v2.users.save': 'Save',
  'v2.users.deletePreset': 'Delete preset {name}',
  'v2.users.segments': 'Segments',
  'v2.users.segmentAll': 'All',
  'v2.segment.new_activated': 'New activated',
  'v2.segment.returning': 'Returning',
  'v2.segment.occasional': 'Occasional',
  'v2.segment.potentially_inactive': 'Potentially inactive',
  'v2.segment.never_activated': 'Never activated',
  'v2.users.snapshot': 'Account facts snapshot: {time} (Belgrade time)',
  'v2.users.empty': 'No account matches these filters.',
  'v2.users.col.account': 'Account',
  'v2.users.col.accountKey': 'Account key',
  'v2.users.col.segment': 'Segment',
  'v2.users.col.score': 'Score',
  'v2.users.col.actionDays': 'Action days 7 / 30',
  'v2.users.col.lastMeaningful': 'Last meaningful action',
  'v2.users.col.lastSignal': 'Last session signal',
  'v2.users.col.lastSignalTitle': 'Last token refresh: the app ran signed in. Not proof of activity.',
  'v2.users.col.registered': 'Registered',
  'v2.users.col.receipts': 'Receipts',
  'v2.users.col.pantry': 'Pantry',
  'v2.users.col.list': 'List',
  'v2.users.col.expenses': 'Expenses',
  'v2.users.shown': '{count} shown',
  'v2.users.pageRange': '{from} to {to} of {total}',
  'v2.score.title': 'How the activity score is computed',
  'v2.score.subtitle': 'Last {days} days. Each action counts up to its daily cap, then {bonus} points per distinct action day. A ranking aid, not a KPI.',
  'v2.score.col.action': 'Action',
  'v2.score.col.pointsEach': 'Points each',
  'v2.score.col.dailyCap': 'Counted per day (max)',

  // Action names (account_action_daily.action)
  'v2.action.receipt_fiscal_imported': 'Fiscal receipt saved as an expense',
  'v2.action.receipt_image_saved': 'Image receipt saved as an expense',
  'v2.action.receipt_image_processed': 'Image receipt processed',
  'v2.action.receipt_image_failed': 'Image receipt failed',
  'v2.action.expense_added_manual': 'Expense added manually',
  'v2.action.budget_set': 'Monthly budget set',
  'v2.action.pantry_item_added': 'Pantry item added by hand',
  'v2.action.pantry_receipt_applied': 'Receipt put in the pantry',
  'v2.action.pantry_receipt_line': 'Receipt line put in the pantry',
  'v2.action.shopping_item_added': 'Shopping-list item added',
  'v2.action.recipe_saved': 'Recipe saved',
  'v2.action.ai_recipe_generated': 'AI recipe generated',
  'v2.action.ai_background_call': 'Background AI call',

  // Plans
  'v2.plan.free': 'free',
  'v2.plan.premium': 'premium',
  'v2.plan.trial': 'trial',

  // Profile
  'v2.profile.back': 'All accounts',
  'v2.profile.description': '{segment} · activity score {score} · {population}',
  'v2.profile.activity': 'Activity',
  'v2.profile.firstMeaningful': 'First meaningful action',
  'v2.profile.lastMeaningful': 'Last meaningful action',
  'v2.profile.actionDays': 'Action days (7 / 30)',
  'v2.profile.lastReceipt': 'Last receipt',
  'v2.profile.receiptsAll': 'Receipts imported (all time)',
  'v2.profile.account': 'Account',
  'v2.profile.accountText': 'Auth facts. Sign-in and token refresh show the app ran signed in, not that the person did anything.',
  'v2.profile.registered': 'Registered',
  'v2.profile.lastSignIn': 'Last sign-in',
  'v2.profile.lastSignal': 'Last session signal',
  'v2.profile.plan': 'Plan',
  'v2.profile.planUntil': '{plan} until {date}',
  'v2.profile.signInMethod': 'Sign-in method',
  'v2.profile.emailVerified': 'E-mail verified',
  'v2.profile.aiCost': 'AI cost (metered)',
  'v2.profile.content': 'Current content',
  'v2.profile.contentText': 'What the account holds today. Deleted items are gone and not counted.',
  'v2.profile.pantryItems': 'Pantry items',
  'v2.profile.shoppingItems': 'Shopping-list items',
  'v2.profile.expenses': 'Expenses',
  'v2.profile.recipes': 'Saved recipes',
  'v2.profile.featuresUsed': 'Features used',
  'v2.profile.byFeature': 'By feature',
  'v2.profile.first': 'First',
  'v2.profile.last': 'Last',
  'v2.profile.scoreBreakdown': 'Score breakdown',
  'v2.profile.scoreText': '{total} points: {dayPoints} for {days} action days, the rest from actions below.',
  'v2.profile.last30': 'Last 30 days',
  'v2.profile.counted': 'Counted',
  'v2.profile.points': 'Points',
  'v2.profile.timeline': 'Timeline',
  'v2.profile.timelineText': 'Actions per Belgrade day. Background actions (not meaningful) are listed but never counted as activity.',
  'v2.profile.actionDayCount_one': '{count} action day',
  'v2.profile.actionDayCount_few': '{count} action days',
  'v2.profile.actionDayCount_other': '{count} action days',
  'v2.profile.day': 'Day',
  'v2.profile.count': 'Count',
  'v2.profile.meaningful': 'Meaningful',
  'v2.profile.noActions': 'No recorded actions.',

  // Cohorts
  'v2.cohorts.notMatured': 'not matured',
  'v2.cohorts.notMaturedTitle': 'No account has reached this point yet',
  'v2.activation.title': 'Account activation',
  'v2.activation.description': 'Production accounts registered in the range, and whether they did something meaningful. Device installs are on the Acquisition page.',
  'v2.activation.registered': 'Registered in range',
  'v2.activation.productionAccounts': 'production accounts',
  'v2.activation.ever': 'Activated (ever)',
  'v2.activation.within1': 'Within 1 day',
  'v2.activation.within1Text': '{activated} of {eligible} with a full day',
  'v2.activation.within7': 'Within 7 days',
  'v2.activation.within7Text': '{activated} of {eligible} with 7 full days',
  'v2.activation.returned': 'Came back after activating',
  'v2.activation.returnedText': '{returned} of {eligible}: a second action day',
  'v2.activation.median': 'Median days to activation',
  'v2.activation.medianText': 'activated accounts only',
  'v2.activation.byFeature': 'Activation by feature',
  'v2.activation.byFeatureText': 'An account is activated in a feature by its first persisted action there. "Within 7 days" counts only accounts registered at least 7 full days before {date}.',
  'v2.activation.featureTable': 'Feature activation',
  'v2.activation.shareRegistered': 'Share of registered',
  'v2.retention.title': 'Account retention',
  'v2.retention.description': 'Registration cohorts of production accounts and the share that came back to do something meaningful.',
  'v2.retention.dayTitle': 'Day N retention',
  'v2.retention.dayText': 'A meaningful action on day N or later. Cells are complete through {date}; the rest are not matured.',
  'v2.retention.weekTitle': 'Weekly retention',
  'v2.retention.weekText': 'Share of the cohort with a meaningful action in week N after registration.',
  'v2.retention.registrationWeek': 'Registration week',
  'v2.retention.activated': 'Activated',
  'v2.retention.dayColumn': 'D{n}',
  'v2.retention.weekColumn': 'W{n}',
  'v2.retention.empty': 'No account registered in this range.',

  // Features
  'v2.features.title': 'Features',
  'v2.features.description': 'Opening a feature and doing something in it are counted separately. Only an action is adoption.',
  'v2.features.devicesTitle': 'Opens and meaningful actions (devices, GA4)',
  'v2.features.devicesText': 'Open to action: devices that opened the feature and then acted, out of devices that opened it.',
  'v2.features.opened': 'Opened',
  'v2.features.acted': 'Acted',
  'v2.features.openedActed': 'Opened and acted',
  'v2.features.openToAction': 'Open to action',
  'v2.features.perDevice': 'Actions per acting device',
  'v2.features.repeat': 'Repeat devices',
  'v2.features.accountsTitle': 'Persisted actions (production accounts)',
  'v2.features.accountsText': 'From saved records, whichever screen the save started from: a receipt scanned on the Smart Hub is Receipts use and Budget and expenses use, without the Budget screen being opened. Saves are confirmations that wrote a record; lines count pantry items and receipt lines. Screen views are not counted here. Deleted records take their history with them, so these are lower bounds.',
  'v2.features.saves': 'Saves',
  'v2.features.lines': 'Lines',
  'v2.features.repeatAccounts': 'Accounts acting on 2+ days',
  'v2.features.noAccounts': 'Account facts are not available for this range.',

  // Receipts
  'v2.receipts.title': 'Receipts',
  'v2.receipts.description': 'Scan attempts on devices, receipts actually saved, and when the purchase happened versus when it was scanned.',
  'v2.receipts.lastFiscal': 'Last successful fiscal receipt',
  'v2.receipts.lastFiscalText': 'production accounts, server time',
  'v2.receipts.lastImage': 'Last image receipt saved',
  'v2.receipts.lastImageText': 'production accounts, device time',
  'v2.receipts.saved': 'Saved receipts',
  'v2.receipts.savedText': 'Persisted in Firestore by production accounts.',
  'v2.receipts.pipeline': 'Image pipeline (backend)',
  'v2.receipts.pipelineText': 'Backend log lines carry no account, so internal tests are included.',
  'v2.receipts.scans': 'Scan attempts by origin (GA4 events)',
  'v2.receipts.scansText': 'Events, except the device columns. The app reports a failure only when the user declines a retry, so most unsuccessful scans appear as "no outcome event".',
  'v2.receipts.origin': 'Origin',
  'v2.receipts.origin.qr': 'QR code',
  'v2.receipts.origin.image': 'Image',
  'v2.receipts.origin.share': 'Share',
  'v2.receipts.started': 'Started',
  'v2.receipts.succeeded': 'Succeeded',
  'v2.receipts.failed': 'Failed',
  'v2.receipts.duplicate': 'Duplicate',
  'v2.receipts.noOutcome': 'No outcome event',
  'v2.receipts.noOutcomeTitle': 'Cancelled, retried, sent to the photo path or app closed: the app sends no event for these',
  'v2.receipts.successOfStarted': 'Succeeded of started',
  'v2.receipts.devicesStarting': 'Devices starting',
  'v2.receipts.devicesSucceeding': 'Devices succeeding',
  'v2.receipts.lastSuccess': 'Last success',
  'v2.receipts.noScans': 'No scan events in this range.',
  'v2.receipts.lagTitle': 'Purchase date versus scan date',
  'v2.receipts.lagText': 'Receipts saved in the range, by how long after the purchase they were saved.',
  'v2.receipts.delay': 'Delay',
  'v2.receipts.fiscal': 'Fiscal',
  'v2.receipts.image': 'Image',
  'v2.receipts.noLag': 'No receipt saved in this range, or receipt facts are not collected.',
  'v2.receipts.lag.same_day': 'Same day',
  'v2.receipts.lag.1_3_days': '1 to 3 days',
  'v2.receipts.lag.4_7_days': '4 to 7 days',
  'v2.receipts.lag.8_30_days': '8 to 30 days',
  'v2.receipts.lag.over_30_days': 'Over 30 days',
  'v2.receipts.lag.purchase_after_save': 'Purchase date after save (clock or OCR error)',
  'v2.receipts.lag.unknown': 'Purchase date unknown',
  'v2.receipts.weeksTitle': 'Receipt lines by purchase week',
  'v2.receipts.weeksText': 'Weeks of the date printed on the receipt, not the scan date. All history.',
  'v2.receipts.purchaseWeek': 'Purchase week',
  'v2.receipts.lines': 'Lines',
  'v2.receipts.noLines': 'No receipt lines collected.',

  // Price search
  'v2.price.title': 'Price search',
  'v2.price.description': 'Backend requests by kind with their outcomes, and searches made in the app. The canary backend is shown apart from production.',
  'v2.price.inApp': 'In the app (GA4)',
  'v2.price.production': 'Production backend',
  'v2.price.canary': 'Canary backend (internal testing)',
  'v2.price.tableText': 'Unterminated = requested minus terminal outcomes; negative when outcomes of requests made before the range fall inside it.',
  'v2.price.canaryFooter': 'Canary traffic comes from internal test builds and is never added to production.',
  'v2.price.kind': 'Kind',
  'v2.price.kind.search': 'search',
  'v2.price.kind.browse': 'browse',
  'v2.price.kind.compare': 'compare',
  'v2.price.requested': 'Requested',
  'v2.price.succeeded': 'Succeeded',
  'v2.price.noResults': 'No results',
  'v2.price.clientErrors': 'Client errors',
  'v2.price.serverErrors': 'Server errors',
  'v2.price.unterminated': 'Unterminated',
  'v2.price.noResultRate': 'No-result rate',
  'v2.price.serverFailureRate': 'Server failure rate',
  'v2.price.avgResults': 'Avg results',
  'v2.price.empty': 'No requests in this range.',

  // Costs
  'v2.costs.title': 'Costs',
  'v2.costs.description': 'Cloud Billing is the authority. App metering is shown next to it as an estimate, never instead of it.',
  'v2.costs.byService': 'By service',
  'v2.costs.byServiceText': 'Usage cost, net of credits; tax and invoice adjustments are separate.',
  'v2.costs.service': 'Service',
  'v2.costs.share': 'Share',
  'v2.costs.noBilling': 'No billing rows in this range.',
  'v2.costs.topSkus': 'Top SKUs',
  'v2.costs.sku': 'SKU',
  'v2.costs.ai': 'AI usage by feature (app metering)',
  'v2.costs.aiText': 'Estimated from token counts and list prices. The billed AI cost above is the authority.',
  'v2.costs.calls': 'Calls',
  'v2.costs.successful': 'Successful',
  'v2.costs.inputTokens': 'Input tokens',
  'v2.costs.outputTokens': 'Output tokens',
  'v2.costs.estimatedCost': 'Estimated cost',
  'v2.costs.noAi': 'No metered AI calls in this range.',

  // Retail
  'v2.retail.title': 'Retail intelligence',
  'v2.retail.description': 'Official price lists of Serbian retail chains, with where each number comes from, how fresh it is and what it cannot tell you.',
  'v2.retail.views': 'Retail views',
  'v2.retail.tab.overview': 'Chains and coverage',
  'v2.retail.tab.categories': 'Categories',
  'v2.retail.tab.changes': 'Price changes',
  'v2.retail.tab.products': 'Products',
  'v2.retail.tab.consumer': 'App-user insights',
  'v2.retail.internalOnly': 'Internal use only',
  'v2.retail.internalOnlyText': 'Commercial delivery of this data is disabled until a release policy and a legal review of the source licences are approved. Resale rights are not assumed.',
  'v2.retail.snapshot': 'Snapshot',
  'v2.retail.built': 'built {time}',
  'v2.retail.chains': 'Chains',
  'v2.retail.chainsText': 'with active offers',
  'v2.retail.activeOffers': 'Active offers',
  'v2.retail.excluded': '{count} excluded (unknown or retired sources)',
  'v2.retail.products': 'Products',
  'v2.retail.comparable': '{count} comparable across 3+ chains',
  'v2.retail.promoOffers': 'Promotional offers',
  'v2.retail.promoText': 'discount below the regular price',
  'v2.retail.chainsTitle': 'Chains',
  'v2.retail.chainsTableText': 'Price index: 100 = market median over identical GTINs sold by 3+ chains. Below 100 is cheaper.',
  'v2.retail.chain': 'Chain',
  'v2.retail.freshness': 'Freshness',
  'v2.retail.latestList': 'Latest price list',
  'v2.retail.lastImport': 'Last import',
  'v2.retail.priceIndex': 'Price index',
  'v2.retail.matched': 'Matched products',
  'v2.retail.licence': 'Licence',
  'v2.retail.indexNaTitle': 'Too few comparable products, or the price list is too old',
  'v2.retail.freshness.FRESH': 'Fresh',
  'v2.retail.freshness.STALE': 'Stale',
  'v2.retail.freshness.TOO_STALE': 'Too stale',
  'v2.retail.freshness.unknown': 'Unknown',
  'v2.retail.freshnessTitle.FRESH': 'The latest price list is recent.',
  'v2.retail.freshnessTitle.STALE': 'The latest price list is older than expected but still used.',
  'v2.retail.freshnessTitle.TOO_STALE': 'The price list is too old to confirm: left out of price comparisons, still counted in coverage.',
  'v2.retail.licence.sodl': 'Open Data Licence of Serbia',
  'v2.retail.licence.unknown': 'Unknown',
  'v2.retail.limitations': 'Limitations',
  'v2.retail.categoriesText': 'Median unit prices over chains with a usable package size. Cheapest chain by its category price index.',
  'v2.retail.perKg': 'Median per kg',
  'v2.retail.perLitre': 'Median per litre',
  'v2.retail.cheapestChain': 'Cheapest chain',
  'v2.retail.itsIndex': 'Its index',
  'v2.retail.allChains': 'All retail chains',
  'v2.retail.changesTitle': 'Weekly price changes',
  'v2.retail.changesText': 'A change is a different regular price for the same chain and product than in the previous snapshot.',
  'v2.retail.changes': 'Changes',
  'v2.retail.increases': 'Increases',
  'v2.retail.decreases': 'Decreases',
  'v2.retail.medianChange': 'Median change',
  'v2.retail.appeared': 'Offers appeared',
  'v2.retail.withdrawn': 'Offers withdrawn',
  'v2.retail.noHistory': 'No price history yet for this selection.',
  'v2.retail.searchProducts': 'Search products',
  'v2.retail.productPlaceholder': 'Product name or GTIN',
  'v2.retail.productCount_one': '{count} product',
  'v2.retail.productCount_few': '{count} products',
  'v2.retail.productCount_other': '{count} products',
  'v2.retail.productsText': 'Prices in RSD at the latest snapshot, median over each chain\'s store labels.',
  'v2.retail.product': 'Product',
  'v2.retail.lowest': 'Lowest',
  'v2.retail.median': 'Median',
  'v2.retail.highest': 'Highest',
  'v2.retail.spread': 'Spread',
  'v2.retail.cheapest': 'Cheapest',
  'v2.retail.noProducts': 'No comparable product matches.',
  'v2.retail.minChars': 'Search at least 3 characters.',
  'v2.consumer.suppressed': 'suppressed',
  'v2.consumer.suppressedTitle': 'Too few contributors, or one contributor dominates',
  'v2.consumer.notice': 'Customer-derived',
  'v2.consumer.noticeText': 'A small, self-selected group of app users. It describes them, not the market. {published} cells published, {suppressed} suppressed.',
  'v2.consumer.categoriesTitle': 'Purchased categories by week',
  'v2.consumer.categoriesText': 'Receipt lines by purchase week. Counts rounded to 5, values to 3 significant figures.',
  'v2.consumer.lines': 'Lines',
  'v2.consumer.value': 'Value',
  'v2.consumer.empty': 'No published cells in this range.',
  'v2.consumer.intentTitle': 'Shopping-list intent by week',
  'v2.consumer.intentText': 'Items added to shopping lists. Intent, not purchases.',
  'v2.consumer.items': 'Items',
  'v2.consumer.basketsTitle': 'Categories bought together',
  'v2.consumer.basketsText': 'Pairs of categories on the same receipt, by purchase month.',
  'v2.consumer.month': 'Month',
  'v2.consumer.categoryA': 'Category A',
  'v2.consumer.categoryB': 'Category B',
  'v2.consumer.baskets': 'Baskets',
};

export const sr: Record<keyof typeof en, string> = {
  'v2.trend.gaps': 'Dani koje izvor ne pokriva su praznine, a ne nule.',
  'v2.col.feature': 'Funkcija',
  'v2.col.actions': 'Radnje',
  'v2.col.accounts': 'Nalozi',
  'v2.col.lastAction': 'Poslednja radnja',
  'v2.col.week': 'Nedelja',
  'v2.col.category': 'Kategorija',
  'v2.col.chains': 'Lanci',
  'v2.col.offers': 'Ponude',
  'v2.col.products': 'Proizvodi',
  'v2.col.promoShare': 'Udeo akcija',
  'v2.col.cost': 'Trošak',
  'v2.col.source': 'Izvor',

  'v2.exec.title': 'Pregled za upravu',
  'v2.exec.description': 'Ključni brojevi za izabrani period, svaki u poređenju sa prethodnim periodom iste dužine.',
  'v2.exec.devices': 'Uređaji',
  'v2.exec.devicesText': 'Instalacije aplikacije iz GA4 (Google Analytics 4). Gost je uređaj bez naloga.',
  'v2.exec.accounts': 'Nalozi',
  'v2.exec.accountsText': 'Produkcijski Firebase nalozi. Aktivnost je sačuvana radnja u aplikaciji, nikad prijava.',
  'v2.exec.product': 'Proizvod',
  'v2.exec.productText': 'Šta su ljudi radili u aplikaciji.',
  'v2.exec.health': 'Stanje i troškovi',
  'v2.exec.healthText': 'Za troškove je merodavan Cloud Billing.',

  'v2.users.title': 'Korisnici',
  'v2.users.description': 'Nalozi rangirani prema onome što su radili u aplikaciji. Prijava se nikad ne računa kao aktivnost.',
  'v2.users.filters': 'Filteri naloga',
  'v2.users.searchLabel': 'Pretraga naloga',
  'v2.users.placeholderAdmin': 'E-pošta ili ID naloga',
  'v2.users.placeholderAnalyst': 'Ključ naloga (prva 4 ili više znakova)',
  'v2.users.sort': 'Redosled',
  'v2.users.sort.score': 'Skor aktivnosti',
  'v2.users.sort.last_action': 'Poslednja radnja',
  'v2.users.sort.created': 'Najnoviji',
  'v2.users.sort.receipts': 'Računi',
  'v2.users.population': 'Populacija',
  'v2.population.production': 'produkcija',
  'v2.population.internal': 'interni',
  'v2.population.test': 'test',
  'v2.population.all': 'svi',
  'v2.users.savedFilters': 'Sačuvani filteri',
  'v2.users.savedFiltersCount': 'Sačuvani filteri ({count})',
  'v2.users.presetName': 'Naziv sačuvanog filtera',
  'v2.users.presetPlaceholder': 'Naziv',
  'v2.users.save': 'Sačuvaj',
  'v2.users.deletePreset': 'Obriši sačuvani filter {name}',
  'v2.users.segments': 'Segmenti',
  'v2.users.segmentAll': 'Svi',
  'v2.segment.new_activated': 'Novi aktivirani',
  'v2.segment.returning': 'Povratni',
  'v2.segment.occasional': 'Povremeni',
  'v2.segment.potentially_inactive': 'Verovatno neaktivni',
  'v2.segment.never_activated': 'Nikad aktivirani',
  'v2.users.snapshot': 'Presek podataka o nalozima: {time} (beogradsko vreme)',
  'v2.users.empty': 'Nijedan nalog ne odgovara ovim filterima.',
  'v2.users.col.account': 'Nalog',
  'v2.users.col.accountKey': 'Ključ naloga',
  'v2.users.col.segment': 'Segment',
  'v2.users.col.score': 'Skor',
  'v2.users.col.actionDays': 'Dani sa radnjom 7 / 30',
  'v2.users.col.lastMeaningful': 'Poslednja značajna radnja',
  'v2.users.col.lastSignal': 'Poslednji signal sesije',
  'v2.users.col.lastSignalTitle': 'Poslednje osvežavanje tokena: aplikacija je radila sa prijavljenim nalogom. To nije dokaz aktivnosti.',
  'v2.users.col.registered': 'Registrovan',
  'v2.users.col.receipts': 'Računi',
  'v2.users.col.pantry': 'Ostava',
  'v2.users.col.list': 'Lista',
  'v2.users.col.expenses': 'Troškovi',
  'v2.users.shown': 'Prikazano: {count}',
  'v2.users.pageRange': '{from} do {to} od {total}',
  'v2.score.title': 'Kako se računa skor aktivnosti',
  'v2.score.subtitle': 'Poslednjih {days} dana. Svaka radnja se računa do svog dnevnog limita, uz {bonus} poena za svaki različit dan sa radnjom. Pomoć za rangiranje, ne KPI.',
  'v2.score.col.action': 'Radnja',
  'v2.score.col.pointsEach': 'Poena po radnji',
  'v2.score.col.dailyCap': 'Računa se dnevno (najviše)',

  'v2.action.receipt_fiscal_imported': 'Fiskalni račun sačuvan kao trošak',
  'v2.action.receipt_image_saved': 'Račun sa slike sačuvan kao trošak',
  'v2.action.receipt_image_processed': 'Obrađen račun sa slike',
  'v2.action.receipt_image_failed': 'Neuspela obrada računa sa slike',
  'v2.action.expense_added_manual': 'Ručno dodat trošak',
  'v2.action.budget_set': 'Postavljen mesečni budžet',
  'v2.action.pantry_item_added': 'Namirnica ručno dodata u ostavu',
  'v2.action.pantry_receipt_applied': 'Račun prebačen u ostavu',
  'v2.action.pantry_receipt_line': 'Stavka računa prebačena u ostavu',
  'v2.action.shopping_item_added': 'Dodata stavka na listu za kupovinu',
  'v2.action.recipe_saved': 'Sačuvan recept',
  'v2.action.ai_recipe_generated': 'AI recept generisan',
  'v2.action.ai_background_call': 'AI poziv u pozadini',

  'v2.plan.free': 'besplatni',
  'v2.plan.premium': 'premium',
  'v2.plan.trial': 'probni',

  'v2.profile.back': 'Svi nalozi',
  'v2.profile.description': '{segment} · skor aktivnosti {score} · {population}',
  'v2.profile.activity': 'Aktivnost',
  'v2.profile.firstMeaningful': 'Prva značajna radnja',
  'v2.profile.lastMeaningful': 'Poslednja značajna radnja',
  'v2.profile.actionDays': 'Dani sa radnjom (7 / 30)',
  'v2.profile.lastReceipt': 'Poslednji račun',
  'v2.profile.receiptsAll': 'Uvezeni računi (ukupno)',
  'v2.profile.account': 'Nalog',
  'v2.profile.accountText': 'Podaci iz prijave (Auth). Prijava i osvežavanje tokena pokazuju da je aplikacija radila sa prijavljenim nalogom, ne da je osoba nešto uradila.',
  'v2.profile.registered': 'Registrovan',
  'v2.profile.lastSignIn': 'Poslednja prijava',
  'v2.profile.lastSignal': 'Poslednji signal sesije',
  'v2.profile.plan': 'Paket',
  'v2.profile.planUntil': '{plan} do {date}',
  'v2.profile.signInMethod': 'Način prijave',
  'v2.profile.emailVerified': 'E-pošta potvrđena',
  'v2.profile.aiCost': 'AI trošak (izmeren u aplikaciji)',
  'v2.profile.content': 'Trenutni sadržaj',
  'v2.profile.contentText': 'Šta nalog trenutno sadrži. Obrisane stavke više ne postoje i ne broje se.',
  'v2.profile.pantryItems': 'Namirnice u ostavi',
  'v2.profile.shoppingItems': 'Stavke na listi za kupovinu',
  'v2.profile.expenses': 'Troškovi',
  'v2.profile.recipes': 'Sačuvani recepti',
  'v2.profile.featuresUsed': 'Korišćene funkcije',
  'v2.profile.byFeature': 'Po funkciji',
  'v2.profile.first': 'Prva',
  'v2.profile.last': 'Poslednja',
  'v2.profile.scoreBreakdown': 'Struktura skora',
  'v2.profile.scoreText': 'Ukupno {total} poena: {dayPoints} za dane sa radnjom ({days}), ostatak od radnji ispod.',
  'v2.profile.last30': 'Poslednjih 30 dana',
  'v2.profile.counted': 'Uračunato',
  'v2.profile.points': 'Poeni',
  'v2.profile.timeline': 'Hronologija',
  'v2.profile.timelineText': 'Radnje po danu (beogradsko vreme). Pozadinske radnje, koje nisu značajne, navedene su, ali se nikad ne računaju kao aktivnost.',
  'v2.profile.actionDayCount_one': '{count} dan sa radnjom',
  'v2.profile.actionDayCount_few': '{count} dana sa radnjom',
  'v2.profile.actionDayCount_other': '{count} dana sa radnjom',
  'v2.profile.day': 'Dan',
  'v2.profile.count': 'Broj',
  'v2.profile.meaningful': 'Značajna',
  'v2.profile.noActions': 'Nema zabeleženih radnji.',

  'v2.cohorts.notMatured': 'nije sazrelo',
  'v2.cohorts.notMaturedTitle': 'Nijedan nalog još nije stigao do ove tačke',
  'v2.activation.title': 'Aktivacija naloga',
  'v2.activation.description': 'Produkcijski nalozi registrovani u periodu i da li su uradili nešto značajno. Instalacije uređaja su na stranici Instalacije.',
  'v2.activation.registered': 'Registrovani u periodu',
  'v2.activation.productionAccounts': 'produkcijski nalozi',
  'v2.activation.ever': 'Aktivirani (ikada)',
  'v2.activation.within1': 'U roku od 1 dana',
  'v2.activation.within1Text': '{activated} od {eligible} sa punim danom',
  'v2.activation.within7': 'U roku od 7 dana',
  'v2.activation.within7Text': '{activated} od {eligible} sa 7 punih dana',
  'v2.activation.returned': 'Vratili se posle aktivacije',
  'v2.activation.returnedText': '{returned} od {eligible}: drugi dan sa radnjom',
  'v2.activation.median': 'Medijana dana do aktivacije',
  'v2.activation.medianText': 'samo aktivirani nalozi',
  'v2.activation.byFeature': 'Aktivacija po funkciji',
  'v2.activation.byFeatureText': 'Nalog je aktiviran u funkciji svojom prvom sačuvanom radnjom u njoj. „U roku od 7 dana” broji samo naloge registrovane najmanje 7 punih dana pre {date}.',
  'v2.activation.featureTable': 'Aktivacija funkcija',
  'v2.activation.shareRegistered': 'Udeo registrovanih',
  'v2.retention.title': 'Zadržavanje naloga',
  'v2.retention.description': 'Kohorte produkcijskih naloga po registraciji i udeo onih koji su se vratili da urade nešto značajno.',
  'v2.retention.dayTitle': 'Zadržavanje po danu N',
  'v2.retention.dayText': 'Značajna radnja na dan N ili kasnije. Ćelije su potpune zaključno sa {date}; ostale još nisu sazrele.',
  'v2.retention.weekTitle': 'Nedeljno zadržavanje',
  'v2.retention.weekText': 'Udeo kohorte sa značajnom radnjom u N-toj nedelji posle registracije.',
  'v2.retention.registrationWeek': 'Nedelja registracije',
  'v2.retention.activated': 'Aktivirani',
  'v2.retention.dayColumn': 'D{n}',
  'v2.retention.weekColumn': 'N{n}',
  'v2.retention.empty': 'Nijedan nalog nije registrovan u ovom periodu.',

  'v2.features.title': 'Funkcije',
  'v2.features.description': 'Otvaranje funkcije i radnja u njoj broje se odvojeno. Samo radnja znači usvajanje.',
  'v2.features.devicesTitle': 'Otvaranja i značajne radnje (uređaji, GA4)',
  'v2.features.devicesText': 'Od otvaranja do radnje: uređaji koji su otvorili funkciju i zatim nešto uradili, od svih uređaja koji su je otvorili.',
  'v2.features.opened': 'Otvorili',
  'v2.features.acted': 'Uradili radnju',
  'v2.features.openedActed': 'Otvorili i uradili radnju',
  'v2.features.openToAction': 'Od otvaranja do radnje',
  'v2.features.perDevice': 'Radnji po aktivnom uređaju',
  'v2.features.repeat': 'Uređaji sa ponovljenom radnjom',
  'v2.features.accountsTitle': 'Sačuvane radnje (produkcijski nalozi)',
  'v2.features.accountsText': 'Iz sačuvanih zapisa, bez obzira na to sa kog ekrana je čuvanje počelo: račun skeniran na Smart Hub-u je korišćenje Računa i Budžeta i troškova, i kad ekran Budžet nije otvoren. Sačuvani unosi su potvrde koje su upisale zapis; stavke broje namirnice i stavke računa. Pregledi ekrana se ovde ne broje. Obrisani zapisi nose svoju istoriju sa sobom, pa su ovo donje granice.',
  'v2.features.saves': 'Sačuvani unosi',
  'v2.features.lines': 'Stavke',
  'v2.features.repeatAccounts': 'Nalozi sa radnjom u 2 ili više dana',
  'v2.features.noAccounts': 'Podaci o nalozima nisu dostupni za ovaj period.',

  'v2.receipts.title': 'Računi',
  'v2.receipts.description': 'Pokušaji skeniranja na uređajima, stvarno sačuvani računi i kada je kupovina obavljena u odnosu na to kada je račun skeniran.',
  'v2.receipts.lastFiscal': 'Poslednji uspešno uvezen fiskalni račun',
  'v2.receipts.lastFiscalText': 'produkcijski nalozi, vreme servera',
  'v2.receipts.lastImage': 'Poslednji sačuvan račun sa slike',
  'v2.receipts.lastImageText': 'produkcijski nalozi, vreme uređaja',
  'v2.receipts.saved': 'Sačuvani računi',
  'v2.receipts.savedText': 'Sačuvani u Firestore bazi od strane produkcijskih naloga.',
  'v2.receipts.pipeline': 'Obrada slika (bekend)',
  'v2.receipts.pipelineText': 'Zapisi bekenda ne nose nalog, pa su uključeni i interni testovi.',
  'v2.receipts.scans': 'Pokušaji skeniranja po izvoru (GA4 događaji)',
  'v2.receipts.scansText': 'Događaji, osim kolona sa uređajima. Aplikacija prijavljuje neuspeh samo kada korisnik odbije ponovni pokušaj, pa se većina neuspešnih skeniranja vodi kao „bez događaja ishoda”.',
  'v2.receipts.origin': 'Izvor',
  'v2.receipts.origin.qr': 'QR kod',
  'v2.receipts.origin.image': 'Slika',
  'v2.receipts.origin.share': 'Deljenje',
  'v2.receipts.started': 'Započeto',
  'v2.receipts.succeeded': 'Uspešno',
  'v2.receipts.failed': 'Neuspešno',
  'v2.receipts.duplicate': 'Duplikat',
  'v2.receipts.noOutcome': 'Bez događaja ishoda',
  'v2.receipts.noOutcomeTitle': 'Otkazano, ponovljeno, prebačeno na fotografisanje ili je aplikacija zatvorena: aplikacija za ovo ne šalje događaj',
  'v2.receipts.successOfStarted': 'Uspešno od započetih',
  'v2.receipts.devicesStarting': 'Uređaji koji su započeli',
  'v2.receipts.devicesSucceeding': 'Uređaji sa uspehom',
  'v2.receipts.lastSuccess': 'Poslednji uspeh',
  'v2.receipts.noScans': 'Nema događaja skeniranja u ovom periodu.',
  'v2.receipts.lagTitle': 'Datum kupovine u odnosu na datum skeniranja',
  'v2.receipts.lagText': 'Računi sačuvani u periodu, prema tome koliko posle kupovine su sačuvani.',
  'v2.receipts.delay': 'Kašnjenje',
  'v2.receipts.fiscal': 'Fiskalni',
  'v2.receipts.image': 'Sa slike',
  'v2.receipts.noLag': 'U ovom periodu nije sačuvan nijedan račun ili podaci o računima nisu prikupljeni.',
  'v2.receipts.lag.same_day': 'Isti dan',
  'v2.receipts.lag.1_3_days': 'Od 1 do 3 dana',
  'v2.receipts.lag.4_7_days': 'Od 4 do 7 dana',
  'v2.receipts.lag.8_30_days': 'Od 8 do 30 dana',
  'v2.receipts.lag.over_30_days': 'Više od 30 dana',
  'v2.receipts.lag.purchase_after_save': 'Datum kupovine posle čuvanja (greška sata ili OCR-a)',
  'v2.receipts.lag.unknown': 'Datum kupovine nepoznat',
  'v2.receipts.weeksTitle': 'Stavke računa po nedelji kupovine',
  'v2.receipts.weeksText': 'Nedelje po datumu odštampanom na računu, ne po datumu skeniranja. Cela istorija.',
  'v2.receipts.purchaseWeek': 'Nedelja kupovine',
  'v2.receipts.lines': 'Stavke',
  'v2.receipts.noLines': 'Nema prikupljenih stavki računa.',

  'v2.price.title': 'Pretraga cena',
  'v2.price.description': 'Zahtevi bekendu po vrsti, sa ishodima, i pretrage u aplikaciji. Kanarinac (probni bekend) prikazan je odvojeno od produkcije.',
  'v2.price.inApp': 'U aplikaciji (GA4)',
  'v2.price.production': 'Produkcijski bekend',
  'v2.price.canary': 'Kanarinac bekend (interno testiranje)',
  'v2.price.tableText': 'Nezavršeni = zahtevi minus konačni ishodi; negativno kada ishodi zahteva upućenih pre perioda padnu u period.',
  'v2.price.canaryFooter': 'Saobraćaj kanarinca dolazi iz internih test verzija i nikad se ne dodaje produkciji.',
  'v2.price.kind': 'Vrsta',
  'v2.price.kind.search': 'pretraga',
  'v2.price.kind.browse': 'pregled kategorija',
  'v2.price.kind.compare': 'poređenje',
  'v2.price.requested': 'Zahtevano',
  'v2.price.succeeded': 'Uspešno',
  'v2.price.noResults': 'Bez rezultata',
  'v2.price.clientErrors': 'Greške klijenta',
  'v2.price.serverErrors': 'Greške servera',
  'v2.price.unterminated': 'Nezavršeni',
  'v2.price.noResultRate': 'Stopa bez rezultata',
  'v2.price.serverFailureRate': 'Stopa grešaka servera',
  'v2.price.avgResults': 'Prosečno rezultata',
  'v2.price.empty': 'Nema zahteva u ovom periodu.',

  'v2.costs.title': 'Troškovi',
  'v2.costs.description': 'Merodavan je Cloud Billing. Merenje u aplikaciji prikazano je pored njega kao procena, nikad umesto njega.',
  'v2.costs.byService': 'Po servisu',
  'v2.costs.byServiceText': 'Trošak korišćenja, umanjen za kredite; porez i korekcije računa su posebno.',
  'v2.costs.service': 'Servis',
  'v2.costs.share': 'Udeo',
  'v2.costs.noBilling': 'Nema stavki naplate u ovom periodu.',
  'v2.costs.topSkus': 'Najveće SKU stavke',
  'v2.costs.sku': 'SKU',
  'v2.costs.ai': 'Korišćenje AI po funkciji (merenje u aplikaciji)',
  'v2.costs.aiText': 'Procena na osnovu broja tokena i cenovnika. Merodavan je naplaćeni AI trošak iznad.',
  'v2.costs.calls': 'Pozivi',
  'v2.costs.successful': 'Uspešni',
  'v2.costs.inputTokens': 'Ulazni tokeni',
  'v2.costs.outputTokens': 'Izlazni tokeni',
  'v2.costs.estimatedCost': 'Procenjen trošak',
  'v2.costs.noAi': 'Nema izmerenih AI poziva u ovom periodu.',

  'v2.retail.title': 'Maloprodajna analitika',
  'v2.retail.description': 'Zvanični cenovnici trgovinskih lanaca u Srbiji: odakle dolazi svaki broj, koliko je ažuran i šta ne može da pokaže.',
  'v2.retail.views': 'Prikazi maloprodaje',
  'v2.retail.tab.overview': 'Lanci i pokrivenost',
  'v2.retail.tab.categories': 'Kategorije',
  'v2.retail.tab.changes': 'Promene cena',
  'v2.retail.tab.products': 'Proizvodi',
  'v2.retail.tab.consumer': 'Uvidi o korisnicima aplikacije',
  'v2.retail.internalOnly': 'Samo za internu upotrebu',
  'v2.retail.internalOnlyText': 'Komercijalna isporuka ovih podataka je isključena dok se ne odobre pravila objavljivanja i pravna provera licenci izvora. Pravo preprodaje se ne podrazumeva.',
  'v2.retail.snapshot': 'Presek',
  'v2.retail.built': 'izrađen {time}',
  'v2.retail.chains': 'Lanci',
  'v2.retail.chainsText': 'sa aktivnim ponudama',
  'v2.retail.activeOffers': 'Aktivne ponude',
  'v2.retail.excluded': '{count} isključeno (nepoznati ili ugašeni izvori)',
  'v2.retail.products': 'Proizvodi',
  'v2.retail.comparable': '{count} uporedivo u 3 ili više lanaca',
  'v2.retail.promoOffers': 'Akcijske ponude',
  'v2.retail.promoText': 'snižena cena ispod redovne',
  'v2.retail.chainsTitle': 'Lanci',
  'v2.retail.chainsTableText': 'Indeks cena: 100 = medijana tržišta za iste GTIN-ove (bar-kodove) koje prodaju 3 ili više lanaca. Ispod 100 je jeftinije.',
  'v2.retail.chain': 'Lanac',
  'v2.retail.freshness': 'Ažurnost',
  'v2.retail.latestList': 'Poslednji cenovnik',
  'v2.retail.lastImport': 'Poslednji uvoz',
  'v2.retail.priceIndex': 'Indeks cena',
  'v2.retail.matched': 'Upareni proizvodi',
  'v2.retail.licence': 'Licenca',
  'v2.retail.indexNaTitle': 'Premalo uporedivih proizvoda ili je cenovnik prestar',
  'v2.retail.freshness.FRESH': 'Ažurno',
  'v2.retail.freshness.STALE': 'Zastarelo',
  'v2.retail.freshness.TOO_STALE': 'Previše zastarelo',
  'v2.retail.freshness.unknown': 'Nepoznato',
  'v2.retail.freshnessTitle.FRESH': 'Poslednji cenovnik je skorašnji.',
  'v2.retail.freshnessTitle.STALE': 'Poslednji cenovnik je stariji od očekivanog, ali se i dalje koristi.',
  'v2.retail.freshnessTitle.TOO_STALE': 'Cenovnik je prestar da bi se potvrdio: izostavljen je iz poređenja cena, ali se i dalje broji u pokrivenosti.',
  'v2.retail.licence.sodl': 'Licenca za otvorene podatke Republike Srbije',
  'v2.retail.licence.unknown': 'Nepoznata',
  'v2.retail.limitations': 'Ograničenja',
  'v2.retail.categoriesText': 'Medijane jediničnih cena u lancima sa upotrebljivom veličinom pakovanja. Najjeftiniji lanac prema indeksu cena u kategoriji.',
  'v2.retail.perKg': 'Medijana po kg',
  'v2.retail.perLitre': 'Medijana po litru',
  'v2.retail.cheapestChain': 'Najjeftiniji lanac',
  'v2.retail.itsIndex': 'Njegov indeks',
  'v2.retail.allChains': 'Svi trgovinski lanci',
  'v2.retail.changesTitle': 'Nedeljne promene cena',
  'v2.retail.changesText': 'Promena je drugačija redovna cena istog proizvoda u istom lancu nego u prethodnom preseku.',
  'v2.retail.changes': 'Promene',
  'v2.retail.increases': 'Poskupljenja',
  'v2.retail.decreases': 'Pojeftinjenja',
  'v2.retail.medianChange': 'Medijana promene',
  'v2.retail.appeared': 'Nove ponude',
  'v2.retail.withdrawn': 'Povučene ponude',
  'v2.retail.noHistory': 'Za ovaj izbor još nema istorije cena.',
  'v2.retail.searchProducts': 'Pretraga proizvoda',
  'v2.retail.productPlaceholder': 'Naziv proizvoda ili GTIN',
  'v2.retail.productCount_one': '{count} proizvod',
  'v2.retail.productCount_few': '{count} proizvoda',
  'v2.retail.productCount_other': '{count} proizvoda',
  'v2.retail.productsText': 'Cene u dinarima (RSD) u poslednjem preseku, medijana po prodajnim objektima svakog lanca.',
  'v2.retail.product': 'Proizvod',
  'v2.retail.lowest': 'Najniža',
  'v2.retail.median': 'Medijana',
  'v2.retail.highest': 'Najviša',
  'v2.retail.spread': 'Raspon',
  'v2.retail.cheapest': 'Najjeftiniji',
  'v2.retail.noProducts': 'Nijedan uporediv proizvod ne odgovara pretrazi.',
  'v2.retail.minChars': 'Za pretragu unesi najmanje 3 znaka.',
  'v2.consumer.suppressed': 'prikriveno',
  'v2.consumer.suppressedTitle': 'Premalo naloga doprinosi ili jedan nalog preovlađuje',
  'v2.consumer.notice': 'Iz podataka korisnika',
  'v2.consumer.noticeText': 'Mala grupa korisnika aplikacije koja se sama odabrala. Opisuje njih, ne tržište. Objavljeno ćelija: {published}, prikriveno: {suppressed}.',
  'v2.consumer.categoriesTitle': 'Kupljene kategorije po nedelji',
  'v2.consumer.categoriesText': 'Stavke računa po nedelji kupovine. Brojevi su zaokruženi na 5, vrednosti na 3 značajne cifre.',
  'v2.consumer.lines': 'Stavke',
  'v2.consumer.value': 'Vrednost',
  'v2.consumer.empty': 'U ovom periodu nema objavljenih ćelija.',
  'v2.consumer.intentTitle': 'Namera kupovine sa liste po nedelji',
  'v2.consumer.intentText': 'Stavke dodate na liste za kupovinu. Namera, ne kupovina.',
  'v2.consumer.items': 'Stavke',
  'v2.consumer.basketsTitle': 'Kategorije kupljene zajedno',
  'v2.consumer.basketsText': 'Parovi kategorija na istom računu, po mesecu kupovine.',
  'v2.consumer.month': 'Mesec',
  'v2.consumer.categoryA': 'Kategorija A',
  'v2.consumer.categoryB': 'Kategorija B',
  'v2.consumer.baskets': 'Korpe',
};
